/**
 * 音频预处理模块
 * 
 * 提供音频上传前的预处理功能，主要用于：
 * - 多声道转单声道（说话人分离功能要求）
 * - 采样率转换
 * 
 * 使用方式：
 * - 默认开启声道转换
 * - 通过 updateAudioPreprocessConfig({ enableMonoConversion: false }) 关闭
 * - 通过 resetAudioPreprocessConfig() 恢复默认
 * 
 * 删除说明：
 * 如果不再需要此功能，删除此文件后，修改 file-upload.ts 中的 preprocessAudio 调用即可
 */

// ============= 配置 =============

/**
 * 音频预处理配置
 * 用于控制上传前的音频处理行为
 */
export interface AudioPreprocessConfig {
  /** 是否启用多声道转单声道（默认 true） */
  enableMonoConversion: boolean;
  /** 目标采样率，null 表示保持原采样率 */
  targetSampleRate: number | null;
}

/** 默认音频预处理配置 */
export const DEFAULT_AUDIO_PREPROCESS_CONFIG: AudioPreprocessConfig = {
  enableMonoConversion: true,
  targetSampleRate: null, // 保持原采样率
};

/** 全局音频预处理配置（可运行时修改） */
export let audioPreprocessConfig: AudioPreprocessConfig = { ...DEFAULT_AUDIO_PREPROCESS_CONFIG };

/**
 * 更新音频预处理配置
 * @param config 部分配置项
 */
export function updateAudioPreprocessConfig(config: Partial<AudioPreprocessConfig>): void {
  audioPreprocessConfig = { ...audioPreprocessConfig, ...config };
}

/**
 * 重置音频预处理配置为默认值
 */
export function resetAudioPreprocessConfig(): void {
  audioPreprocessConfig = { ...DEFAULT_AUDIO_PREPROCESS_CONFIG };
}

// ============= 类型定义 =============

/** 音频信息 */
export interface AudioInfo {
  /** 声道数 */
  numberOfChannels: number;
  /** 采样率 */
  sampleRate: number;
  /** 时长（秒） */
  duration: number;
}

/** 预处理结果 */
export interface PreprocessResult {
  /** 处理后的文件 */
  file: File;
  /** 是否进行了转换 */
  wasConverted: boolean;
  /** 原始音频信息 */
  originalInfo?: AudioInfo;
}

// ============= 核心功能 =============

/**
 * 获取音频文件信息
 * @param file 音频文件
 * @returns 音频信息
 */
export async function getAudioInfo(file: File): Promise<AudioInfo> {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    return {
      numberOfChannels: audioBuffer.numberOfChannels,
      sampleRate: audioBuffer.sampleRate,
      duration: audioBuffer.duration,
    };
  } finally {
    await audioContext.close();
  }
}

/**
 * 将多声道音频转换为单声道
 * 通过平均混合所有声道的方式实现（保留所有声道信息）
 * 
 * @param file 原始音频文件
 * @param targetSampleRate 目标采样率，null 表示保持原采样率
 * @returns 单声道的 WAV 文件
 */
export async function convertToMono(
  file: File,
  targetSampleRate: number | null = null
): Promise<File> {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // 如果已经是单声道，且不需要转换采样率，直接返回原文件
    const needResample = targetSampleRate && targetSampleRate !== audioBuffer.sampleRate;
    if (audioBuffer.numberOfChannels === 1 && !needResample) {
      return file;
    }
    
    // 确定目标采样率
    const outputSampleRate = targetSampleRate || audioBuffer.sampleRate;
    const outputLength = Math.ceil(audioBuffer.duration * outputSampleRate);
    
    // 直接对多声道进行平均混合（保留所有声道信息）
    const monoData = new Float32Array(audioBuffer.length);
    const numChannels = audioBuffer.numberOfChannels;
    
    // 将所有声道混合平均
    for (let ch = 0; ch < numChannels; ch++) {
      const channelData = audioBuffer.getChannelData(ch);
      for (let i = 0; i < audioBuffer.length; i++) {
        monoData[i] += channelData[i] / numChannels;
      }
    }
    
    // 如果需要重采样，使用 OfflineAudioContext
    let finalData: Float32Array = monoData;
    if (needResample && outputSampleRate !== audioBuffer.sampleRate) {
      const offlineContext = new OfflineAudioContext(1, outputLength, outputSampleRate);
      const tempBuffer = offlineContext.createBuffer(1, audioBuffer.length, audioBuffer.sampleRate);
      tempBuffer.getChannelData(0).set(monoData);
      
      const source = offlineContext.createBufferSource();
      source.buffer = tempBuffer;
      source.connect(offlineContext.destination);
      source.start(0);
      
      const renderedBuffer = await offlineContext.startRendering();
      finalData = new Float32Array(renderedBuffer.getChannelData(0));
    }
    
    // 转换为 WAV 文件
    const wavBlob = encodeWav(finalData, outputSampleRate || audioBuffer.sampleRate);
    
    // 生成新文件名（保持原名，扩展名改为 .wav）
    const originalName = file.name;
    const nameWithoutExt = originalName.replace(/\.[^.]+$/, '');
    const newFileName = `${nameWithoutExt}_mono.wav`;
    
    return new File([wavBlob], newFileName, { type: 'audio/wav' });
  } finally {
    await audioContext.close();
  }
}

/**
 * 音频预处理：根据配置进行声道转换
 * @param file 原始音频文件
 * @param config 预处理配置，不传则使用全局配置
 * @returns 处理后的文件（可能是原文件或转换后的文件）
 */
export async function preprocessAudio(
  file: File,
  config?: Partial<AudioPreprocessConfig>
): Promise<PreprocessResult> {
  const effectiveConfig = { ...audioPreprocessConfig, ...config };
  
  // 如果禁用了声道转换，直接返回原文件
  if (!effectiveConfig.enableMonoConversion) {
    return { file, wasConverted: false };
  }
  
  try {
    // 获取音频信息
    const audioInfo = await getAudioInfo(file);
    
    // 如果已经是单声道，且不需要重采样，直接返回
    const needResample = effectiveConfig.targetSampleRate && 
      effectiveConfig.targetSampleRate !== audioInfo.sampleRate;
    
    if (audioInfo.numberOfChannels === 1 && !needResample) {
      console.log('[AudioPreprocess] 音频已是单声道，无需转换');
      return { file, wasConverted: false, originalInfo: audioInfo };
    }
    
    // 进行声道转换
    console.log(`[AudioPreprocess] 开始转换: ${audioInfo.numberOfChannels}声道 -> 单声道`);
    const convertedFile = await convertToMono(file, effectiveConfig.targetSampleRate);
    console.log(`[AudioPreprocess] 转换完成: ${file.name} -> ${convertedFile.name}`);
    
    return {
      file: convertedFile,
      wasConverted: true,
      originalInfo: audioInfo,
    };
  } catch (error) {
    // 如果转换失败，记录错误但返回原文件，让后端处理
    console.warn('[AudioPreprocess] 声道转换失败，使用原文件:', error);
    return { file, wasConverted: false };
  }
}

// ============= 内部工具函数 =============

/**
 * 将 Float32Array 音频数据编码为 WAV Blob
 */
function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numChannels = 1; // 单声道
  const format = 1; // PCM
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  
  const dataSize = samples.length * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;
  
  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);
  
  // WAV 文件头
  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, 'WAVE');
  
  // fmt 块
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  
  // data 块
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);
  
  // 写入音频数据
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    view.setInt16(offset, intSample, true);
    offset += 2;
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/**
 * 向 DataView 写入字符串
 */
function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
